import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Form, Input, InputNumber, Button, Card, Typography, Space, Result, Image } from 'antd';
import { UserOutlined, MailOutlined, DollarOutlined, PrinterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { createWallet } from '../../services/adminWalletService';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

export default function CreateWalletPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [walletId, setWalletId] = useState('');
  const [walletName, setWalletName] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });
    return unsubscribe;
  }, [navigate]);

  const handleSubmit = async (values: { name: string; email: string; initialBalance: number }) => {
    setLoading(true);

    try {
      const result = await createWallet({
        ownerName: values.name,
        ownerEmail: values.email,
        initialBalance: values.initialBalance || 0,
      });

      setWalletId(result.walletId);
      setWalletName(values.name);

      const qrUrl = await QRCode.toDataURL(result.qrCodeData);
      setQrCodeUrl(qrUrl);
    } catch (error: any) {
      alert('Erro ao criar carteira: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (qrCodeUrl) {
    return (
      <Layout>
        <Card style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', background: colors.cardBackground }}>
          <Result
            status="success"
            title="Carteira Criada com Sucesso!"
            subTitle={`Carteira de ${walletName}`}
            extra={
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Image
                  src={qrCodeUrl}
                  alt="QR Code"
                  style={{ maxWidth: 250 }}
                  preview={false}
                />
                <Text code style={{ fontSize: 12 }}>
                  ID da Carteira: {walletId}
                </Text>
                <Space>
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={handlePrint}
                    size="large"
                  >
                    Imprimir QR Code
                  </Button>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/admin/wallets')}
                    size="large"
                    style={{ background: colors.success, color: '#fff', borderColor: colors.success }}
                  >
                    Voltar para Carteiras
                  </Button>
                </Space>
              </Space>
            }
          />
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <Title level={2} style={{ marginBottom: 24 }}>Criar Nova Carteira</Title>

      <Card style={{ maxWidth: 600, background: colors.cardBackground }}>
        <Form
          name="createWallet"
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
          initialValues={{ initialBalance: 0 }}
        >
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Por favor, insira o nome do proprietário' }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: colors.textDescription }} />}
              placeholder="Nome do proprietário"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              { required: true, message: 'Por favor, insira o e-mail' },
              { type: 'email', message: 'Por favor, insira um e-mail válido' },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: colors.textDescription }} />}
              placeholder="proprietario@email.com"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="initialBalance"
            label="Saldo Inicial (R$)"
          >
            <InputNumber
              prefix={<DollarOutlined style={{ color: colors.textDescription }} />}
              placeholder="0,00"
              size="large"
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{ background: colors.success, borderColor: colors.success }}
              >
                Criar Carteira
              </Button>
              <Button
                size="large"
                onClick={() => navigate('/admin/wallets')}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Layout>
  );
}
